import { Button } from '@progress/kendo-react-buttons';
import { Dialog } from '@progress/kendo-react-dialogs';
import { Input } from '@progress/kendo-react-inputs';
import { Error } from '@progress/kendo-react-labels';
import { EquipmentListContext } from 'app/components/ReactContexts/equipmentListContext';
import { axiosInstance } from 'services/api';
import _ from 'lodash';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { IGeofenceEntry } from 'types/IGeofenceEntry';
import { tagEvent } from 'utils/ga';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm } from 'react-hook-form';
import clsx from 'clsx';
import { GlobalErrorContext } from 'app/components/ReactContexts/globalErrorContext';

const schema = z.object({
  name: z
    .string()
    .trim()
    .min(1, { error: 'field_is_required' })
    .max(45, { error: 'maximum_length_exceeded' }),
});

type FormData = z.infer<typeof schema>;

interface Props {
  index: number;
  geofence: IGeofenceEntry;
  closeDialog: () => void;
}

export function EditGeofenceName(props: Props) {
  const { t } = useTranslation();

  const { setEquipmentListData: setData } =
    React.useContext(EquipmentListContext);
  const { addErrorModal } = React.useContext(GlobalErrorContext);

  const {
    control,
    formState: { errors },
    handleSubmit,
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    values: {
      name: props.geofence.description,
    },
  });

  const [isLoading, setIsLoading] = React.useState(false);

  const onSubmit = ({ name }: FormData) => {
    setIsLoading(true);
    axiosInstance
      .put(
        `/api/user/geofence`,
        JSON.stringify({
          id: props.geofence.id,
          description: name,
          points: props.geofence.points,
        }),
        {
          headers: {
            'Content-Type': 'application/json',
          },
        },
      )
      .then(() => {
        setData(oldData => ({
          ...oldData,
          geofenceList: _.orderBy(
            oldData.geofenceList?.map((geof, ind) => {
              if (ind !== props.index) {
                return geof;
              }
              return {
                id: props.geofence.id,
                description: name,
                points: props.geofence.points,
              };
            }),
            e => e.description.toLowerCase(),
            ['asc'],
          ),
        }));
        tagEvent('edit_geofence_name');
        props.closeDialog();
      })
      .catch(e => {
        addErrorModal();
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  return (
    <Dialog className="modal-form">
      <div className="modal-container relative">
        <span
          className="back-button absolute"
          onClick={() => {
            props.closeDialog();
          }}
        >
          <svg
            id="ic_nav_back"
            xmlns="http://www.w3.org/2000/svg"
            width="48"
            height="48"
            viewBox="0 0 48 48"
          >
            <rect id="Box" width="48" height="48" fill="none" />
            <path
              id="ic_nav_up"
              d="M16.465,8.482H1.483"
              transform="translate(15.518 15.518)"
              fill="none"
              stroke="#1a1a1a"
              strokeLinecap="round"
              strokeWidth="2"
            />
            <path
              id="Path_470"
              data-name="Path 470"
              d="M23.676,16.018,16.2,23.748l7.477,7.73"
              transform="translate(0.324 0.252)"
              fill="none"
              stroke="#1a1a1a"
              strokeLinecap="round"
              strokeWidth="2"
            />
          </svg>
        </span>
        <>
          <h2>{t('edit_geofence_name')}</h2>
          <div className="form-caption">{t('you_can_rename')}</div>

          <form onSubmit={handleSubmit(onSubmit)}>
            <div className={clsx('form-group', { error: !!errors.name })}>
              <div className="form-content">
                <div className="form-label">
                  <span>{t('geofence_name')}</span>
                </div>
                <div className="form-control">
                  <Controller
                    name="name"
                    control={control}
                    render={({ field }) => (
                      <Input
                        {...field}
                        placeholder={t('enter_geofence_name')}
                        disabled={isLoading}
                      />
                    )}
                  />
                </div>
              </div>

              {errors.name?.message && (
                <div className="form-error">
                  <Error>
                    {t<string>(errors.name.message, { length: 45 })}
                  </Error>
                </div>
              )}
            </div>

            <Button
              themeColor="primary"
              type="submit"
              disabled={isLoading || !!errors.name}
            >
              {isLoading ? (
                <div className="loading_icon loading_icon-btn">
                  <div className="css-icon"></div>
                </div>
              ) : (
                t('save_changes')
              )}
            </Button>
          </form>
        </>
      </div>
    </Dialog>
  );
}
